import type { AdvanceProvisionRule } from ".";

/**
 * Same blocking rules as the backend (`getAdvanceProvisionRulesError`), plus gap detection which the
 * backend accepts (months in a gap get a 0 % rate).
 */
export type GridCheck =
    | { kind: "invalid" }
    | { kind: "overlap"; first: AdvanceProvisionRule; second: AdvanceProvisionRule }
    | { kind: "missingInfinite" }
    | { kind: "gap"; from: number; to: number }
    | { kind: "complete" };

const isValidRate = (rate: number) => Number.isFinite(rate) && rate >= 0 && rate <= 100;

export const checkGrid = (rules: AdvanceProvisionRule[]): GridCheck => {
    const isInvalid = rules.some(
        (rule) =>
            !isValidRate(rule.rate) ||
            !Number.isInteger(rule.startMonth) ||
            rule.startMonth < 0 ||
            (rule.endMonth !== null && (!Number.isInteger(rule.endMonth) || rule.endMonth < rule.startMonth)),
    );
    if (isInvalid) return { kind: "invalid" };

    const active = rules.filter((rule) => rule.isActive).sort((a, b) => a.startMonth - b.startMonth);
    for (let i = 1; i < active.length; i++) {
        const previous = active[i - 1];
        if (previous.endMonth === null || active[i].startMonth <= previous.endMonth) {
            return { kind: "overlap", first: previous, second: active[i] };
        }
    }
    // An empty grid is refused too (the backend would otherwise fall back to the defaults)
    if (!active.length || active[active.length - 1].endMonth !== null) return { kind: "missingInfinite" };

    let expectedStart = 0;
    for (const rule of active) {
        if (rule.startMonth > expectedStart) return { kind: "gap", from: expectedStart, to: rule.startMonth - 1 };
        expectedStart = (rule.endMonth ?? Infinity) + 1;
    }
    return { kind: "complete" };
};

export const isBlockingGridCheck = (check: GridCheck) =>
    check.kind === "invalid" || check.kind === "overlap" || check.kind === "missingInfinite";

export const checkOverrides = (rules: AdvanceProvisionRule[]): "invalid" | "valid" =>
    rules.every((rule) => !!rule.label?.trim() && rule.conditionType === "term_ended" && isValidRate(rule.rate))
        ? "valid"
        : "invalid";

/** "4–12" / "61+" */
export const formatMonthRange = ({ startMonth, endMonth }: { startMonth: number; endMonth: number | null }) =>
    endMonth === null ? `${startMonth}+` : `${startMonth}–${endMonth}`;
